import type { HistoricalMonthReturn } from "@repo/shared/types";

export type ToDatePeriod =
  | { unit: "month"; year: number; month: number }
  | { unit: "quarter"; year: number; quarter: number }
  | { unit: "year"; year: number };

/** The periods the Statistics footnote names as partial. */
export type PartialPeriods = {
  /**
   * The running month (or its quarter, in the Quarters grid), measured to date.
   * For a series ended at an old last price, the period that last price leaves
   * partial.
   */
  toDate: ToDatePeriod | null;
  /** The first month, when the history does not start in January. */
  start: { year: number; month: number } | null;
};

/**
 * Partial periods are the running month with its quarter and year, and, when
 * the history does not start in January, its first quarter and year. The
 * to-date period is named in the unit of the grid on show.
 *
 * QL-736 — `ended`: the series ends at an old last price. A last price on a
 * month-end completes its month, but its quarter and year can still be
 * partial, so the footnote names the first of those that is.
 */
export function partialPeriods(
  months: HistoricalMonthReturn[],
  mode: "months" | "quarters",
  { ended = false }: { ended?: boolean } = {},
): PartialPeriods {
  const first = months[0];
  const latest = months.at(-1);
  const start = first && first.month !== 1 ? { year: first.year, month: first.month } : null;
  if (!latest) return { toDate: null, start };
  const quarter = Math.ceil(latest.month / 3);
  let toDate: ToDatePeriod | null = null;
  if (latest.partial) {
    toDate =
      mode === "quarters"
        ? { unit: "quarter", year: latest.year, quarter }
        : { unit: "month", year: latest.year, month: latest.month };
  } else if (ended && mode === "quarters" && latest.month % 3 !== 0) {
    toDate = { unit: "quarter", year: latest.year, quarter };
  } else if (ended && latest.month !== 12) {
    toDate = { unit: "year", year: latest.year };
  }
  return { toDate, start };
}
